import { useResizeObserver } from '@react-aria/utils';
import { useCallback, useRef, useState, ReactNode } from 'react';
import {
  ListBox,
  ListBoxItem,
  Popover,
  PopoverProps,
  Selection,
  Key
} from 'react-aria-components';
import { VariantProps } from 'tailwind-variants';

import { useCloseListboxOnOutsideClick, useCloseListboxUnderHeader } from './hooks';
import { styles } from './UiKitListbox.styles';

export type ClassNameState = VariantProps<typeof styles>['state'];

export interface BaseOption {
  label: string;
  value: string;
  icon?: string;
}

export type Option<T = object> = BaseOption & T;

export interface UiKitListboxProps<T extends Option> extends PopoverProps {
  options: T[];
  renderOption?: (option: T) => ReactNode;
  selectionMode?: 'single' | 'multiple';
  selectedKeys?: Iterable<Key>;
  onSelectionChange?: (keys: Selection) => void;
  disabledKeys?: Iterable<Key>;
  isOpen?: boolean;
  renderListBox?: (options: T[], optionClassName: string) => ReactNode;
  width?: string | null;
  className?: string;
  optionClassName?: string;
  classNameState?: ClassNameState;
}

export function UiKitListbox<T extends Option>({
  options,
  triggerRef,
  renderOption,
  selectionMode = 'single',
  selectedKeys,
  onSelectionChange,
  disabledKeys,
  isOpen,
  renderListBox,
  width = null,
  className,
  optionClassName,
  classNameState = 'default',
  isNonModal,
  onOpenChange,
  ...props
}: UiKitListboxProps<T>) {
  const slots = styles({ state: classNameState });
  const optionClasses = slots.option({
    className: optionClassName,
    state: classNameState
  });

  const [menuWidth, setMenuWidth] = useState<string | null>(width);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);
  const popoverRef = useRef<HTMLElement>(null);

  const onResize = useCallback(() => {
    if (triggerRef?.current) {
      setPortalContainer(triggerRef.current.closest('.dialog'));
    } else {
      setPortalContainer(null);
    }

    if (width) {
      setMenuWidth(width);
      return;
    }

    const calculateMenuWidth = () => {
      if (!triggerRef?.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      setMenuWidth(`${triggerRect.width}px`);
    };

    calculateMenuWidth();
  }, [triggerRef, width]);

  useResizeObserver({
    ref: triggerRef,
    onResize
  });

  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  useCloseListboxOnOutsideClick({
    isOpen: !!isOpen,
    triggerRef,
    popoverRef,
    onClose: close,
    enabled: isNonModal
  });
  useCloseListboxUnderHeader({
    isOpen: !!isOpen,
    triggerRef,
    onClose: close,
    enabled: isNonModal
  });

  return (
    <Popover
      ref={popoverRef}
      triggerRef={triggerRef}
      isOpen={isOpen}
      isNonModal={isNonModal}
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      boundaryElement={portalContainer ?? undefined}
      className={slots.base({ className })}
      offset={1}
      style={{ '--menu-width': menuWidth } as React.CSSProperties}
      onOpenChange={isNonModal ? undefined : onOpenChange}
      {...props}
    >
      {renderListBox ? (
        renderListBox(options, optionClasses)
      ) : (
        <ListBox
          className={slots.listbox()}
          items={options}
          selectionMode={selectionMode}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          disabledKeys={disabledKeys}
        >
          {(item) => (
            <ListBoxItem
              key={item.value}
              id={item.value}
              className={optionClasses}
              textValue={item.label}
            >
              {renderOption ? renderOption(item) : item.label}
            </ListBoxItem>
          )}
        </ListBox>
      )}
    </Popover>
  );
}
